import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Collapse,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { EmailAccount, ProviderInfo, ACCOUNT_COLORS } from './types';
import { apiService } from '../../services/api';

interface EmailAccountFormProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  editingAccount?: EmailAccount | null;
}

const EmailAccountForm: React.FC<EmailAccountFormProps> = ({
  open,
  onClose,
  onSaved,
  editingAccount,
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [accountName, setAccountName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [color, setColor] = useState(ACCOUNT_COLORS[0]);
  const [showPassword, setShowPassword] = useState(false);
  const [provider, setProvider] = useState<ProviderInfo | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [imapHost, setImapHost] = useState('');
  const [imapPort, setImapPort] = useState(993);
  const [imapSecure, setImapSecure] = useState(true);
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState(587);
  const [smtpSecure, setSmtpSecure] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ imap: { success: boolean; error?: string }; smtp: { success: boolean; error?: string } } | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditing = !!editingAccount;

  useEffect(() => {
    if (!open) return;
    setPassword('');
    setShowPassword(false);
    setTestResult(null);
    setTesting(false);
    setSaving(false);
    setShowAdvanced(false);
    setError(null);

    if (editingAccount) {
      setEmail(editingAccount.email);
      setAccountName(editingAccount.accountName || '');
      setDisplayName(editingAccount.displayName || '');
      setColor(editingAccount.color || ACCOUNT_COLORS[0]);
      setImapHost(editingAccount.imapHost || '');
      setImapPort(editingAccount.imapPort || 993);
      setImapSecure(editingAccount.imapSecure !== false);
      setSmtpHost(editingAccount.smtpHost || '');
      setSmtpPort(editingAccount.smtpPort || 587);
      setSmtpSecure(editingAccount.smtpSecure || false);
    } else {
      setEmail('');
      setAccountName('');
      setDisplayName('');
      setColor(ACCOUNT_COLORS[Math.floor(Math.random() * ACCOUNT_COLORS.length)]);
      setImapHost('');
      setImapPort(993);
      setImapSecure(true);
      setSmtpHost('');
      setSmtpPort(587);
      setSmtpSecure(false);
      setProvider(null);
    }
  }, [open, editingAccount]);

  const handleEmailChange = async (val: string) => {
    setEmail(val);
    setTestResult(null);
    if (val.includes('@') && val.includes('.')) {
      try {
        const result = await apiService.detectEmailProvider(val);
        const prov = (result as any).data as ProviderInfo;
        setProvider(prov);
        if (prov.imap?.host) {
          setImapHost(prov.imap.host);
          setImapPort(prov.imap.port);
          setImapSecure(prov.imap.secure);
        }
        if (prov.smtp?.host) {
          setSmtpHost(prov.smtp.host);
          setSmtpPort(prov.smtp.port);
          setSmtpSecure(prov.smtp.secure);
        }
        if (!accountName) {
          setAccountName(val.split('@')[0]);
        }
      } catch {}
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await apiService.testEmailConnection({
        email,
        password,
        imapHost,
        imapPort,
        imapSecure,
        smtpHost,
        smtpPort,
        smtpSecure,
      });
      setTestResult((result as any).data);
    } catch (err: any) {
      setTestResult({
        imap: { success: false, error: err.message },
        smtp: { success: false, error: err.message },
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      if (isEditing) {
        const updateData: Record<string, any> = {
          accountName,
          displayName: displayName || undefined,
          color,
        };
        if (password) {
          updateData.password = password;
          updateData.imapHost = imapHost;
          updateData.imapPort = imapPort;
          updateData.imapSecure = imapSecure;
          updateData.smtpHost = smtpHost;
          updateData.smtpPort = smtpPort;
          updateData.smtpSecure = smtpSecure;
        }
        await apiService.updateEmailAccount(editingAccount!.id, updateData);
      } else {
        await apiService.saveEmailSettings({
          email,
          password,
          accountName,
          displayName: displayName || undefined,
          provider: provider?.provider || 'custom',
          color,
          imapHost,
          imapPort,
          imapSecure,
          smtpHost,
          smtpPort,
          smtpSecure,
        });
      }
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const testPassed = testResult?.imap?.success && testResult?.smtp?.success;
  const canSave = isEditing
    ? (accountName || displayName) // Editing: can save without re-testing if not changing password
    : testPassed; // New: must pass test

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditing ? 'Edit Email Account' : 'Add Email Account'}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Account Name */}
        <TextField
          fullWidth
          label="Account Name"
          placeholder="e.g. Support, Sales, Hello"
          value={accountName}
          onChange={(e) => setAccountName(e.target.value)}
          sx={{ mb: 2, mt: 1 }}
        />

        {/* Color picker */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Color
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {ACCOUNT_COLORS.map((c) => (
              <Box
                key={c}
                onClick={() => setColor(c)}
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  bgcolor: c,
                  cursor: 'pointer',
                  border: color === c ? '3px solid' : '2px solid transparent',
                  borderColor: color === c ? 'text.primary' : 'transparent',
                  transition: 'border 0.15s',
                  '&:hover': { opacity: 0.8 },
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Email input */}
        <TextField
          fullWidth
          label="Email Address"
          placeholder="hello@trylifescribe.com"
          value={email}
          onChange={(e) => handleEmailChange(e.target.value)}
          disabled={isEditing}
          sx={{ mb: 2 }}
        />

        {/* Gmail App Password alert */}
        {provider?.requiresAppPassword && !isEditing && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {provider.name} requires an App Password
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Your regular password won't work. Follow these steps:
            </Typography>
            <Box component="ol" sx={{ pl: 2, mb: 1, '& li': { mb: 0.5, fontSize: '0.85rem' } }}>
              <li>
                Go to{' '}
                <a href={provider.securityUrl || '#'} target="_blank" rel="noopener noreferrer">
                  Google Account Security
                </a>{' '}
                and enable <strong>2-Step Verification</strong>
              </li>
              <li>
                Then go to{' '}
                <a href={provider.appPasswordUrl || '#'} target="_blank" rel="noopener noreferrer">
                  App Passwords
                </a>
              </li>
              <li>Create an App Password (select "Mail" or "Other")</li>
              <li>Copy the 16-character code and paste it below</li>
            </Box>
          </Alert>
        )}

        {/* Password input */}
        <TextField
          fullWidth
          label={isEditing ? 'New Password (leave blank to keep current)' : (provider?.requiresAppPassword ? 'App Password' : 'Password')}
          placeholder={provider?.requiresAppPassword ? 'xxxx xxxx xxxx xxxx' : 'Email password'}
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => { setPassword(e.target.value); setTestResult(null); }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {/* Display name */}
        <TextField
          fullWidth
          label="Display Name (optional)"
          placeholder="LifeScribe Support"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          sx={{ mb: 2 }}
        />

        {/* Advanced settings toggle */}
        {!isEditing && (
          <>
            <Button
              size="small"
              onClick={() => setShowAdvanced(!showAdvanced)}
              endIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ mb: 1, textTransform: 'none', color: 'text.secondary' }}
            >
              {showAdvanced ? 'Hide' : 'Show'} Advanced Settings
            </Button>

            <Collapse in={showAdvanced}>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>IMAP Settings (Incoming)</Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField label="Host" value={imapHost} onChange={(e) => setImapHost(e.target.value)} size="small" sx={{ flex: 2 }} />
                  <TextField label="Port" type="number" value={imapPort} onChange={(e) => setImapPort(parseInt(e.target.value) || 993)} size="small" sx={{ flex: 1 }} />
                  <FormControlLabel control={<Switch checked={imapSecure} onChange={(e) => setImapSecure(e.target.checked)} size="small" />} label="SSL" sx={{ ml: 0 }} />
                </Box>
                <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>SMTP Settings (Outgoing)</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField label="Host" value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} size="small" sx={{ flex: 2 }} />
                  <TextField label="Port" type="number" value={smtpPort} onChange={(e) => setSmtpPort(parseInt(e.target.value) || 587)} size="small" sx={{ flex: 1 }} />
                  <FormControlLabel control={<Switch checked={smtpSecure} onChange={(e) => setSmtpSecure(e.target.checked)} size="small" />} label="SSL" sx={{ ml: 0 }} />
                </Box>
              </Box>
            </Collapse>
          </>
        )}

        {/* Test Connection button (only for new or if password changed) */}
        {(!isEditing || password) && (
          <Button
            variant="outlined"
            onClick={handleTestConnection}
            disabled={testing || !email || !password || !imapHost || !smtpHost}
            startIcon={testing ? <CircularProgress size={16} /> : undefined}
            fullWidth
            sx={{ mb: 2 }}
          >
            {testing ? 'Testing...' : 'Test Connection'}
          </Button>
        )}

        {/* Test results */}
        {testResult && (
          <Box sx={{ mb: 2 }}>
            <Alert severity={testResult.imap.success ? 'success' : 'error'} sx={{ mb: 1 }}>
              <strong>IMAP:</strong> {testResult.imap.success ? 'Connected successfully' : testResult.imap.error}
            </Alert>
            <Alert severity={testResult.smtp.success ? 'success' : 'error'}>
              <strong>SMTP:</strong> {testResult.smtp.success ? 'Connected successfully' : testResult.smtp.error}
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || (!isEditing && !testPassed)}
          startIcon={saving ? <CircularProgress size={16} /> : <LinkIcon />}
          sx={{ bgcolor: '#8B5CF6', '&:hover': { bgcolor: '#7C3AED' } }}
        >
          {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Save & Connect'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailAccountForm;
